import { useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";

export default function PhotoExpandModal({
  photos,
  currentIndex,
  onClose,
  onPrev,
  onNext,
}) {
  const current = photos[currentIndex];
  const isBlurred = current?.blurred;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 animate-in fade-in duration-300"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/20 transition-colors"
        aria-label="Закрыть"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Photo count indicator */}
      {photos.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md text-white text-xs font-semibold">
          {currentIndex + 1} / {photos.length}
        </div>
      )}

      {/* Navigation arrows */}
      {photos.length > 1 && onPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/20 transition-colors"
          aria-label="Предыдущее"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {photos.length > 1 && onNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/20 transition-colors"
          aria-label="Следующее"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Dots indicator */}
      {photos.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); onPrev && i < currentIndex ? onPrev() : onNext && i > currentIndex ? onNext() : null; }}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentIndex ? "bg-white w-5" : "bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}

      {/* Image — respect blurred flag */}
      {isBlurred ? (
        <div className="relative max-w-full max-h-full flex items-center justify-center px-4" onClick={(e) => e.stopPropagation()}>
          <img
            src={current?.data_url}
            alt=""
            className="max-w-full max-h-[80vh] object-contain select-none photo-blur"
            draggable={false}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Camera className="w-10 h-10 text-white/60 mb-3" />
            <div className="text-white font-[Onest] font-bold text-lg text-center px-6">
              Загрузи ещё фото
            </div>
            <div className="text-white/60 text-sm mt-1 text-center px-6">
              Чтобы увидеть все фото, загрузи свои
            </div>
          </div>
        </div>
      ) : (
        <img
          src={current?.data_url}
          alt=""
          className="max-w-full max-h-full object-contain select-none px-4"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />
      )}
    </div>
  );
}